import { MEAL_PLAN_NEW_USER_PROMO_CODES } from 'common/Constants';
import {
  MealPlanPromoCodeAppliedFragment,
  PromoCodeFieldsFragment,
  PromoCodeType,
} from 'src/gqlReactTypings.generated.d';
import { usePromotionalsHook } from 'src/promotionals/usePromotionalsHook';
import { useCurrentUser } from 'src/shared/hooks/useCurrentUserHook';
import { useLocation } from 'src/shared/hooks/useLocation';
import { Routes } from 'src/shared/Routes';
import { locationInRoutes } from 'src/shared/utils/RouteUtilities';
import { match, P } from 'ts-pattern';

/**
 *
 * @property {string} discountText - The text representation of the discount to be applied, i.e. '20%' or '$20'.
 * @property {string} totaledDiscountText - The total discount text considering the order count.
 * @property {boolean} isPercentageDiscount - Indicates if the discount is a percentage or a fixed amount.
 * @property {number} discount - The amount of discount to be applied.
 * @property {PromoCodeFieldsFragment | null} referralPromo - The referral promo code details or null if not applicable.
 * @property {string} orderCountText - The text indicating the order count for which the discount is applicable.
 * @property {number} orderCount - The number of orders for which the discount is applicable.
 * @property {string} numOrdersText - The text indicating the number of orders.
 * @property {string} referralPromoText - The text for the referral promo discount.
 * @property {string} promoText - The general promo text.
 * @property {(priceInCents: number) => number} applyDiscountToPrice - A function to apply the discount to a given
 *   price in cents.
 */

export const getPromoDiscountLabel = (promoCodeType?: PromoCodeType | string | null, code?: string | null): string => {
  if (promoCodeType === PromoCodeType.Referral) return 'Referral promo';
  if (promoCodeType === PromoCodeType.ReferralReward) return 'Referral reward';
  if (code && MEAL_PLAN_NEW_USER_PROMO_CODES.includes(code)) return 'First order promo';
  return 'Promo';
};

type MealPlanDiscount = {
  discountText: string;
  totaledDiscountText: string;
  isPercentageDiscount: boolean;
  discount: number;
  mealPlanPromoCode: PromoCodeFieldsFragment | null;
  numOrdersText: string;
  promoText: string;
  promoEndText: string;
  weekText: string;
  promoLineLabel: string;
  promoCodeString: string | null;
};
export const getDiscountText = (discount: number, isPercentage: boolean) =>
  match({ isPercentageDiscount: isPercentage, discount })
    .with({ isPercentageDiscount: true, discount: P.not(0) }, () => `${discount}%`)
    .with({ isPercentageDiscount: false, discount: P.not(0) }, () => `$${Math.floor(discount / 100)}`)
    .otherwise(() => '');

export const useMealPlanDiscount = (promoCodeApplied?: MealPlanPromoCodeAppliedFragment | null): MealPlanDiscount => {
  const location = useLocation();
  const isOnMealPlanEditOrder = locationInRoutes(location, [Routes.CONSUMER_MEAL_PLANS_EDIT_ORDER]);
  const { availablePromos, code } = usePromotionalsHook();
  const [currentUser, isCurrentUserLoading] = useCurrentUser();

  const defaultReferralPromo = [...availablePromos, code].find((promo) => promo?.promoCodeType === 'REFERRAL') ?? null;
  const selectedCode = availablePromos.find((promo) => promo?.code === code?.code) ?? null;
  const shouldUseReferralPromo =
    defaultReferralPromo != null &&
    defaultReferralPromo?.maxUsesPerUser === defaultReferralPromo?.usesRemainingForUser &&
    defaultReferralPromo?.isMealPlanEnabled;

  // Go through availablePromos, if there's one for meal plans, show it in the discount

  // Prioritize the passed in promo code, then the promo code in state, then the default code
  let codeToUse;
  if (isOnMealPlanEditOrder) {
    codeToUse = promoCodeApplied;
  } else if (promoCodeApplied?.isMealPlanEnabled) {
    codeToUse = promoCodeApplied;
  } else if (selectedCode?.isMealPlanEnabled) {
    codeToUse = selectedCode;
  } else if (shouldUseReferralPromo) {
    codeToUse = defaultReferralPromo;
  }

  const isConfirmedFirstTimeMealPlanUser =
    !isCurrentUserLoading && (currentUser == null || currentUser.isFirstTimeMealPlanUser);
  const shouldSuppressFirstTimePromo =
    !isOnMealPlanEditOrder &&
    !isConfirmedFirstTimeMealPlanUser &&
    MEAL_PLAN_NEW_USER_PROMO_CODES.includes(codeToUse?.code ?? '');
  if (shouldSuppressFirstTimePromo) {
    codeToUse = undefined;
  }

  let discount = codeToUse?.value ?? 0;
  if (codeToUse?.remainingBalance && codeToUse?.remainingBalance < discount) {
    discount = codeToUse.remainingBalance;
  }

  const maxUses = defaultReferralPromo?.maxUsesPerUser ?? 0;
  const isFirstOrderOnly = codeToUse?.firstPromoOnly ?? false;

  const isPercentageDiscount = codeToUse?.isPercentage ?? false;

  const discountText = getDiscountText(discount, isPercentageDiscount);

  const orderCount = shouldUseReferralPromo ? maxUses : 1;
  const totaledDiscountText = match({ isPercentageDiscount, discount })
    .with({ isPercentageDiscount: true, discount: P.not(0) }, () => `${discount}%`)
    .with({ isPercentageDiscount: false, discount: P.not(0) }, () => `$${Math.floor((discount * orderCount) / 100)}`)
    .otherwise(() => '');
  const isMultipleWeekDiscount = shouldUseReferralPromo && maxUses > 1 && !isFirstOrderOnly;
  const orderCountText = isMultipleWeekDiscount ? `first ${maxUses}` : 'first';
  const deliveryPluralityText = isMultipleWeekDiscount ? 'deliveries' : 'delivery';
  const numOrdersText = `${orderCountText} weekly ${deliveryPluralityText}`;
  const promoText = `Get ${discountText} off your ${numOrdersText}!`;
  const promoEndText = `welcome offer`;
  const weekText = isMultipleWeekDiscount ? `first ${maxUses} weeks` : 'first week';

  const promoLineLabel = getPromoDiscountLabel(codeToUse?.promoCodeType, codeToUse?.code);
  const promoCodeString =
    codeToUse?.code && !MEAL_PLAN_NEW_USER_PROMO_CODES.includes(codeToUse?.code) ? codeToUse?.code ?? null : null;

  return {
    discountText,
    totaledDiscountText,
    isPercentageDiscount,
    discount,
    mealPlanPromoCode: codeToUse,
    numOrdersText,
    promoText,
    promoEndText,
    weekText,
    promoLineLabel,
    promoCodeString,
  };
};
